import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuthContext } from '../context/AuthContext';
import { usePublicProfile } from '../hooks/usePublicProfile';
import { usePublicPickHistory } from '../hooks/usePublicPickHistory';
import { useFriendshipStatus } from '../hooks/useFriendshipStatus';
import { sendFriendRequest, acceptFriendRequest, removeFriend } from '../services/friendService';
import { BADGES, ACHIEVEMENT_BADGE_IDS, ROUTES } from '../utils/constants';
import PickHistoryRow from '../components/PickHistoryRow';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';
import './UserProfilePage.css';
import './ProfilePage.css';
import './MyPicksPage.css';

const STATS_FILTERS = [
  { key: 'all', label: 'All Time' },
  { key: 'week', label: 'This Week' },
  { key: 'month', label: 'This Month' },
  { key: 'year', label: 'This Year' },
];

const DATE_FILTERS = [
  { key: 'all', label: 'All Picks' },
  { key: '7', label: 'Last 7 Days' },
  { key: '30', label: 'Last 30 Days' },
  { key: '90', label: 'Last 90 Days' },
];

function formatMemberSince(createdAt) {
  if (!createdAt) return '';
  const ms = typeof createdAt.toMillis === 'function'
    ? createdAt.toMillis()
    : typeof createdAt.seconds === 'number'
    ? createdAt.seconds * 1000
    : new Date(createdAt).getTime();
  return new Date(ms).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
}

function toMs(ts) {
  if (!ts) return 0;
  if (typeof ts.toMillis === 'function') return ts.toMillis();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  return new Date(ts).getTime();
}

export default function UserProfilePage() {
  const { username } = useParams();
  const { user } = useAuthContext();
  const { profile, loading: profileLoading, notFound } = usePublicProfile(username);
  const { picks, loading: picksLoading } = usePublicPickHistory(profile?.uid);
  const { status: friendStatus, loading: friendStatusLoading, requestId, friendshipId, refetch: refetchFriendStatus } = useFriendshipStatus(profile?.uid);
  const [statsFilter, setStatsFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [friendActionLoading, setFriendActionLoading] = useState(false);

  const handleAddFriend = async () => {
    if (!profile?.username) return;
    setFriendActionLoading(true);
    try {
      await sendFriendRequest(profile.username);
      toast.success(`Friend request sent to ${profile.username}!`);
      refetchFriendStatus();
    } catch (err) {
      toast.error(err.message || 'Failed to send friend request');
    } finally {
      setFriendActionLoading(false);
    }
  };

  const handleAcceptRequest = async () => {
    if (!requestId) return;
    setFriendActionLoading(true);
    try {
      await acceptFriendRequest(requestId);
      toast.success(`You are now friends with ${profile.username}!`);
      refetchFriendStatus();
    } catch (err) {
      toast.error(err.message || 'Failed to accept request');
    } finally {
      setFriendActionLoading(false);
    }
  };

  const handleRemoveFriend = async () => {
    if (!friendshipId) return;
    if (!window.confirm(`Remove ${profile.username} from your friends?`)) return;
    setFriendActionLoading(true);
    try {
      await removeFriend(friendshipId);
      toast.success(`Removed ${profile.username} from friends`);
      refetchFriendStatus();
    } catch (err) {
      toast.error(err.message || 'Failed to remove friend');
    } finally {
      setFriendActionLoading(false);
    }
  };

  // Filter to resolved picks only (won/lost/push), exclude cancelled
  const resolvedPicks = useMemo(() => {
    return picks.filter((p) => p.status !== 'cancelled');
  }, [picks]);

  // Compute stats with time period filters (mirrors StatsPage logic)
  const filteredStats = useMemo(() => {
    if (!profile) return null;

    // For "All Time", use denormalized profile values
    if (statsFilter === 'all') {
      const wins = profile.totalCorrect || 0;
      const total = profile.totalPicks || 0;
      const losses = total - wins;
      const winPct = total > 0 ? Math.round((wins / total) * 100) : 0;
      return {
        currentStreak: profile.currentStreak || 0,
        longestStreak: profile.longestStreak || 0,
        record: `${wins}-${losses < 0 ? 0 : losses}`,
        winPct: `${winPct}%`,
      };
    }

    // Calculate cutoff date based on filter
    const now = new Date();
    let cutoff;
    if (statsFilter === 'week') {
      const day = now.getDay();
      const diff = day === 0 ? 6 : day - 1;
      cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate() - diff);
    } else if (statsFilter === 'month') {
      cutoff = new Date(now.getFullYear(), now.getMonth(), 1);
    } else {
      cutoff = new Date(now.getFullYear(), 0, 1);
    }
    const cutoffMs = cutoff.getTime();

    const filtered = resolvedPicks.filter((p) => {
      if (p.status !== 'won' && p.status !== 'lost' && p.status !== 'push') return false;
      const ts = p.resolvedAt || p.createdAt;
      return toMs(ts) >= cutoffMs;
    });

    const wins = filtered.filter((p) => p.status === 'won').length;
    const losses = filtered.filter((p) => p.status === 'lost').length;
    const total = wins + losses;
    const winPct = total > 0 ? Math.round((wins / total) * 100) : 0;

    // Calculate streaks from chronologically sorted picks
    const sorted = [...filtered].sort((a, b) => {
      return toMs(a.resolvedAt || a.createdAt) - toMs(b.resolvedAt || b.createdAt);
    });

    let currentStreak = 0;
    let longestStreak = 0;
    for (const p of sorted) {
      if (p.status === 'won') {
        currentStreak++;
        if (currentStreak > longestStreak) longestStreak = currentStreak;
      } else if (p.status === 'lost') {
        currentStreak = 0;
      }
    }

    return {
      currentStreak,
      longestStreak,
      record: `${wins}-${losses}`,
      winPct: `${winPct}%`,
    };
  }, [profile, statsFilter, resolvedPicks]);

  // Apply date range filter to pick history (mirrors MyPicksPage logic)
  const filteredPicks = useMemo(() => {
    if (dateFilter === 'all') return resolvedPicks;

    const days = parseInt(dateFilter, 10);
    const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;

    return resolvedPicks.filter((p) => {
      const ts = p.resolvedAt || p.createdAt;
      return toMs(ts) >= cutoff;
    });
  }, [resolvedPicks, dateFilter]);

  if (profileLoading) {
    return (
      <div className="user-profile-page">
        <LoadingSpinner message="Loading profile..." />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="user-profile-page">
        <div className="profile-not-found">
          <h1>User Not Found</h1>
          <p>The user "{username}" doesn't exist.</p>
          <Link to={ROUTES.ARENA}>Back to Arena</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="user-profile-page">
      {/* Profile Header */}
      <div className="user-profile-header">
        <span className="user-profile-badge-emoji">
          {BADGES[profile.activeBadge]?.emoji || '🐣'}
        </span>
        <div className="user-profile-header-info">
          <h1>{profile.username}</h1>
          <p className="member-since">
            Member since {formatMemberSince(profile.createdAt)}
          </p>
        </div>
        {/* Friend Action Button */}
        {user && !friendStatusLoading && friendStatus !== 'self' && (
          <div className="user-profile-friend-action">
            {friendStatus === 'none' && (
              <button
                className="btn-profile-add-friend"
                onClick={handleAddFriend}
                disabled={friendActionLoading}
              >
                {friendActionLoading ? '...' : '+ Add Friend'}
              </button>
            )}
            {friendStatus === 'request_sent' && (
              <span className="btn-profile-pending">Request Sent</span>
            )}
            {friendStatus === 'request_received' && (
              <button
                className="btn-profile-accept"
                onClick={handleAcceptRequest}
                disabled={friendActionLoading}
              >
                {friendActionLoading ? '...' : 'Accept Request'}
              </button>
            )}
            {friendStatus === 'friends' && (
              <div className="profile-friends-actions">
                <span className="btn-profile-friends">Friends ✓</span>
                <button
                  className="btn-profile-remove"
                  onClick={handleRemoveFriend}
                  disabled={friendActionLoading}
                >
                  Remove
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Stats Section */}
      {filteredStats && (
        <div className="stats-card">
          <h2>Stats</h2>
          <div className="stats-filters">
            {STATS_FILTERS.map((f) => (
              <button
                key={f.key}
                className={`stats-filter-btn ${statsFilter === f.key ? 'active' : ''}`}
                onClick={() => setStatsFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="stats-grid">
            <div className="stat-item">
              <span className="stat-value">{filteredStats.currentStreak}</span>
              <span className="stat-label">Current Streak</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{filteredStats.longestStreak}</span>
              <span className="stat-label">Longest Streak</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{filteredStats.record}</span>
              <span className="stat-label">W-L Record</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{filteredStats.winPct}</span>
              <span className="stat-label">Win %</span>
            </div>
          </div>
        </div>
      )}

      {/* Pick History Section */}
      <div className="picks-section">
        <div className="history-header">
          <h2>Pick History</h2>
          <select
            className="date-filter-select"
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          >
            {DATE_FILTERS.map((f) => (
              <option key={f.key} value={f.key}>{f.label}</option>
            ))}
          </select>
        </div>
        {picksLoading ? (
          <LoadingSpinner message="Loading picks..." />
        ) : filteredPicks.length === 0 ? (
          <div className="no-pick-card">
            <p>No completed picks yet.</p>
          </div>
        ) : (
          <div className="pick-history-table-wrap">
            <table className="pick-history-table">
              <thead>
                <tr>
                  <th>Sport</th>
                  <th>Team</th>
                  <th>Result</th>
                  <th>Streak</th>
                </tr>
              </thead>
              <tbody>
                {filteredPicks.map((pick) => (
                  <PickHistoryRow key={pick.id} pick={pick} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Achievements Section */}
      <div className="picks-section">
        <h2>Achievements</h2>
        <div className="badge-grid">
          {ACHIEVEMENT_BADGE_IDS.map((id) => {
            const badge = BADGES[id];
            const isEarned = profile.badges.includes(id);
            return (
              <div
                key={id}
                className={`badge-tile ${isEarned ? '' : 'badge-locked'}`}
                title={isEarned ? badge.name : `${badge.name} — ${badge.description}`}
              >
                <span className="badge-tile-emoji">{isEarned ? badge.emoji : '🔒'}</span>
                <span className="badge-tile-name">{badge.name}</span>
                <span className="badge-tile-criteria">{badge.description}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
